import { invalidateTypedQueries, useApiClient } from '@/hooks/useApiClient'
import type { components } from '@/types/api/v1'
import { DeleteFilled, DeleteOutlined, ToolFilled, ToolOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import type { ICellRendererParams } from 'ag-grid-community'
import { Dropdown, Flex, Popover, Tag, Tooltip } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { uniq } from 'lodash'
import { useCallback, useMemo, useState } from 'react'
import { useScheduleItems } from '../Context/ScheduleItems/ScheduleItemsContext'
import type { ScheduleItemWithConflicts } from '../Context/ScheduleItems/scheduleItemStore'
import { InfoCellRenderer } from '../Grid/CellRenderers/InfoCellRenderer'
import { TractorIcon, TrailerIcon } from '../Icons/Icons'
import { OutOfServiceForm, type OutOfServiceFormInit } from './OutOfServiceForm'

type Data = components['schemas']['TractorView']

const TrailerNumbers = ({ value }: { value: ScheduleItemWithConflicts[] }) => {
  const values = useMemo(
    () => uniq(value.map((x) => x.trailer_number).filter((x): x is string => x !== undefined && x !== null)),
    [value]
  )
  if (!values.length) return null
  const hasMultipleTrailers = values.length !== 1
  return (
    <Tooltip title={hasMultipleTrailers ? values.join(', ') : null} trigger='hover'>
      <Flex gap={6} align='center' style={{ flexDirection: hasMultipleTrailers ? 'row-reverse' : 'row' }}>
        <TrailerIcon />
        {hasMultipleTrailers ? `(${values.length})` : values[0]}
      </Flex>
    </Tooltip>
  )
}

type OutOfServiceStatus = {
  start: Dayjs
  end: Dayjs
  reason?: string
  id?: string
  notes?: string
}

const StatusTag = ({ value }: { value?: OutOfServiceStatus }) => {
  if (!value) return null
  const { start, end, reason } = value

  const title = reason && reason !== 'oos' ? `OOS: ${reason}` : 'Out of Service'
  let label = start.format('M/DD')
  if (start.date() !== end.date()) {
    label += ` - ${end.format('M/DD')}`
  }
  return (
    <Tooltip title={title}>
      <Tag icon={<ToolFilled />} color='var(--theme-warning)' style={{ margin: 0 }}>
        {label}
      </Tag>
    </Tooltip>
  )
}

const formatDateForEp = (date: Dayjs) => date.tz('UTC').startOf('day').toISOString()

const useOutOfService = ({
  onMutate,
  tractorId,
}: {
  onMutate: () => void
  tractorId?: string
}) => {
  const { search } = useScheduleItems<Data>()
  const api = useApiClient()
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, variables } = api.useMutation('post', '/tractor/set_out_of_service', {
    onMutate,
    onSettled: () => invalidateTypedQueries(queryClient, 'post', '/driver/schedule/overview'),
  })
  const status = useMemo((): OutOfServiceStatus | undefined => {
    if (isPending && variables?.body.schedule) {
      return {
        start: dayjs(variables.body.schedule.start_date),
        end: dayjs(variables.body.schedule.end_date),
        reason: variables.body.reason_code ?? undefined,
        notes: variables.body.note ?? undefined,
      }
    }
    if (!tractorId) return
    const item = search({ index: 'tractor', value: tractorId }, { index: 'driver', value: '' })
      .filter((x) => x.shift === 'oos')
      .at(0)
    if (!item) return
    return {
      start: item.start,
      end: item.end,
      reason: item.out_of_service_reason ?? undefined,
      id: item.event_id,
      notes: item.out_of_service_note ?? undefined,
    }
  }, [search, tractorId, variables?.body, isPending])
  return {
    mutateAsync,
    isPending,
    status,
  }
}

const useInactive = ({ tractorId }: { tractorId?: string }) => {
  const { search } = useScheduleItems()
  const api = useApiClient()
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, variables } = api.useMutation('post', '/tractor/set_inactive', {
    onSettled: () => invalidateTypedQueries(queryClient, 'post', '/driver/schedule/overview'),
  })

  const status = useMemo(() => {
    if (isPending) return variables.body.as_of_date ? dayjs(variables.body.as_of_date) : undefined
    if (!tractorId) return
    const item = search({ index: 'tractor', value: tractorId }, { index: 'driver', value: '' })
      .filter((x) => x.shift === 'inactive')
      .at(0)
    return item?.start
  }, [isPending, search, tractorId, variables?.body])

  const toggleInactive = useCallback(() => {
    if (!tractorId) return
    if (status) {
      return mutateAsync({ body: { tractor_id: tractorId, as_of_date: null } })
    }
    return mutateAsync({ body: { tractor_id: tractorId, as_of_date: formatDateForEp(dayjs()) } })
  }, [mutateAsync, status, tractorId])

  return {
    toggleInactive,
    mutateAsync,
    isPending,
    status,
  }
}

export function TractorInfoCellRenderer({ data }: ICellRendererParams<Data>) {
  const { search } = useScheduleItems<Data>()
  const schedules = useMemo(() => search({ index: 'tractor', value: data?.id ?? '' }), [search, data?.id])
  const [popover, setPopover] = useState(false)
  const { mutateAsync: setOOS, status: statusTag } = useOutOfService({
    onMutate: () => setPopover(false),
    tractorId: data?.id,
  })
  const { toggleInactive, status: inactiveDate } = useInactive({ tractorId: data?.id })

  const defaultOutOfServiceStatus = useMemo((): OutOfServiceFormInit => {
    if (!statusTag) return { dateRange: [dayjs(), dayjs().add(7, 'days')] }
    return {
      dateRange: [statusTag.start, statusTag.end],
      reasonCode: statusTag.reason,
      id: statusTag.id,
      notes: statusTag.notes,
    }
  }, [statusTag])

  if (!data) return null
  return (
    <Popover
      open={popover}
      destroyTooltipOnHide
      trigger='click'
      placement='right'
      styles={{ root: { maxWidth: '300px' } }}
      content={
        <OutOfServiceForm
          equipment={{
            id: data.id,
            label: `${data.tractor_number} (${data.depot ?? 'No Location'})`,
            type: 'tractor',
          }}
          initialValue={defaultOutOfServiceStatus}
          onSubmit={(val) =>
            setOOS({
              body: {
                reason_code: val.reasonCode,
                tractor_id: data.id,
                schedule: {
                  start_date: formatDateForEp(val.start),
                  end_date: formatDateForEp(val.end),
                },
                note: val.notes,
              },
            })
          }
          onDelete={() => setOOS({ body: { tractor_id: data.id, schedule: null } })}
          onCancel={() => setPopover(false)}
        />
      }
      onOpenChange={(v) => (!v ? setPopover(v) : null)}
    >
      <Dropdown
        menu={{
          items: [
            {
              icon: <ToolOutlined />,
              key: 'oos',
              label: `${statusTag ? 'Edit' : 'Set'} Out of Service`,
              onClick: () => setPopover(true),
            },
            {
              icon: <DeleteOutlined />,
              key: 'inactive',
              label: `Mark as ${inactiveDate ? 'Active' : 'Inactive'}`,
              onClick: toggleInactive,
            },
          ],
        }}
        trigger={['click']}
      >
        <InfoCellRenderer
          title={
            <Flex gap={4} align='center'>
              <TractorIcon />
              {data.tractor_number}
            </Flex>
          }
          action={<TrailerNumbers value={schedules} />}
          subtitle={data.depot ?? 'No location'}
          tag={
            inactiveDate ? (
              <Tooltip title='Inactive'>
                <Tag icon={<DeleteFilled />} color='var(--theme-error)' style={{ margin: 0 }}>
                  {inactiveDate.format('M/DD')}
                </Tag>
              </Tooltip>
            ) : (
              <StatusTag value={statusTag} />
            )
          }
        />
      </Dropdown>
    </Popover>
  )
}
